import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, MapPin } from "lucide-react";
import { useSite } from "@/context/AppContext";
import { api } from "@/lib/api";
import { Container, Seo, SectionTitle } from "@/components/site/Ui";
import { CategoryCard, TreatmentCard } from "@/components/site/Cards";
import BeforeAfter from "@/components/site/BeforeAfter";

function Hero({ site }) {
  return (
    <section className="relative overflow-hidden">
      <Container className="grid items-center gap-12 py-14 lg:grid-cols-12 lg:py-24">
        <div className="lg:col-span-6">
          <p className="eyebrow reveal">Medicina estetica</p>
          <h1 className="reveal reveal-1 mt-5 text-4xl leading-[1.04] sm:text-5xl lg:text-6xl">
            La bellezza che ti somiglia, <em className="text-[var(--petrol)]">curata con misura.</em>
          </h1>
          <p className="reveal reveal-2 mt-6 max-w-lg text-base text-[var(--muted-ink)] md:text-lg">
            {site?.center_name || "Morphe Aesthetic"} è un centro di medicina estetica dove ogni percorso nasce da un ascolto attento e da una valutazione medica individuale.
          </p>
          <div className="reveal reveal-3 mt-10 flex flex-wrap gap-4">
            <Link to="/prenota" className="btn-primary" data-testid="hero-book-btn">Prenota una consulenza <ArrowRight size={18} /></Link>
            <Link to="/trattamenti" className="btn-outline" data-testid="hero-treatments-btn">Scopri i trattamenti</Link>
          </div>
        </div>
        <div className="relative lg:col-span-6">
          <img src="/brand/rosa.webp" alt="" aria-hidden="true" width={397} height={700} className="pointer-events-none absolute -left-16 -top-10 z-10 hidden w-36 opacity-90 lg:block xl:-left-24 xl:w-44" />
          <div className="grain relative overflow-hidden rounded-[2rem]">
            <img src="/img/centro-7265.webp" alt="Poltroncine in velluto petrolio nella sala d'attesa del centro" width={1200} height={1800} fetchpriority="high" className="aspect-[4/5] w-full object-cover sm:aspect-[5/5] lg:aspect-[4/5]" />
          </div>
          <div className="absolute -bottom-6 left-6 hidden rounded-2xl border border-[var(--line)] bg-white/95 p-5 shadow-xl backdrop-blur sm:block">
            <p className="eyebrow">Orari</p>
            {(site?.opening_hours || []).slice(0, 2).map((h) => (
              <p key={h.day} className="mt-1 text-sm"><span className="font-medium">{h.day}</span> · {h.hours}</p>
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
}

function Philosophy() {
  return (
    <section className="overflow-hidden bg-[var(--ivory-soft)] py-24">
      <Container className="grid items-center gap-14 lg:grid-cols-2">
        <div className="relative">
          <img src="/brand/rosa.webp" alt="" aria-hidden="true" width={397} height={700} loading="lazy" className="pointer-events-none absolute -bottom-10 right-0 z-10 w-24 sm:-right-6 sm:w-32 lg:-right-14" />
          <img src="/img/centro-7271.webp" alt="Sala d'attesa con poltroncine, tavolini e dipinto" width={1200} height={1800} loading="lazy" className="aspect-[4/5] w-full rounded-[2rem] object-cover lg:max-w-md" />
        </div>
        <div>
          <SectionTitle eyebrow="La nostra filosofia" title="Ascolto, misura, naturalezza." />
          <span className="brass-rule mt-8" />
          <p className="mt-8 max-w-lg leading-relaxed text-[var(--muted-ink)]">Non esistono protocolli uguali per tutti. Valutiamo insieme obiettivi e aspettative, spieghiamo con trasparenza cosa è possibile ottenere e proponiamo solo ciò che è indicato per te.</p>
          <ol className="mt-10 grid gap-6 sm:grid-cols-3">
            {["Colloquio", "Valutazione medica", "Percorso su misura"].map((s, i) => (
              <li key={s}><span className="font-serif text-4xl text-[var(--brass-text)]">0{i + 1}</span><p className="mt-2 text-sm font-medium">{s}</p></li>
            ))}
          </ol>
          <Link to="/chi-siamo" className="mt-10 inline-flex items-center gap-2 text-[var(--petrol)] hover:underline" data-testid="home-about-link">Chi siamo <ArrowRight size={16} /></Link>
        </div>
      </Container>
    </section>
  );
}

function GalleryTeaser() {
  const [item, setItem] = useState(null);
  useEffect(() => {
    api.get("/public/gallery", { params: { per: 1 } }).then((r) => setItem(r.data.items[0] || null)).catch(() => {});
  }, []);
  if (!item) return null;
  return (
    <section className="py-24">
      <Container className="grid items-center gap-14 lg:grid-cols-2">
        <div>
          <SectionTitle eyebrow="Prima e dopo" title="Risultati raccontati con trasparenza." intro="Pubblichiamo solo casi autorizzati, senza ritocchi. Trascina il divisore per confrontare." />
          <Link to="/galleria" className="btn-outline mt-8" data-testid="home-gallery-link">Vedi la galleria</Link>
        </div>
        <div className="mx-auto w-full max-w-md">
          <BeforeAfter beforeId={item.before_id} afterId={item.after_id} beforeAlt={item.before_alt} afterAlt={item.after_alt} testid="home-before-after" />
          <p className="mt-3 text-sm text-[var(--muted-ink)]">{item.title}</p>
        </div>
      </Container>
    </section>
  );
}

function Visit({ site }) {
  return (
    <section className="bg-[var(--petrol)] py-20 text-[var(--ivory)]">
      <Container className="grid gap-10 md:grid-cols-3">
        <div className="md:col-span-2">
          <p className="eyebrow !text-[var(--brass)]">Il centro</p>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl">Un'unica sede, pensata per accoglierti.</h2>
          <p className="mt-5 flex items-center gap-2 text-ivory/85"><MapPin size={18} />{site?.address_street}, {site?.address_city}</p>
        </div>
        <div className="space-y-2 text-sm">
          <p className="flex items-center gap-2 font-medium"><Clock size={16} /> Orari</p>
          {(site?.opening_hours || []).map((h) => <p key={h.day} className="text-ivory/85">{h.day}: {h.hours}</p>)}
          <Link to="/il-centro" className="mt-4 inline-flex rounded-full border border-ivory/60 px-5 py-2.5 hover:bg-[var(--ivory)] hover:text-[var(--petrol)]" data-testid="home-center-link">Come raggiungerci</Link>
        </div>
      </Container>
    </section>
  );
}

export default function Home() {
  const { site, catalog } = useSite();
  const cats = catalog?.categories || [];
  const featured = (catalog?.treatments || []).filter((t) => t.kind === "treatment").slice(0, 3);
  const jsonLd = site?.center_name && {
    "@context": "https://schema.org", "@type": "MedicalClinic", name: site.center_name, url: window.location.origin,
    ...(site.address_street && !site.address_street.startsWith("[") ? { address: { "@type": "PostalAddress", streetAddress: site.address_street, addressLocality: site.address_city, postalCode: site.address_zip, addressCountry: "IT" } } : {}),
    ...(site.phone && !site.phone.startsWith("[") ? { telephone: site.phone } : {}),
  };
  return (
    <>
      <Seo title="Medicina estetica su misura" description="Centro di medicina estetica: acido ialuronico, botulino, trattamenti corpo e qualità della pelle. Prenota online." path="/" jsonLd={jsonLd} />
      <Hero site={site} />
      <section className="py-24">
        <Container>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionTitle eyebrow="Aree di trattamento" title="Scegli da dove iniziare." />
            <Link to="/trattamenti" className="text-[var(--petrol)] hover:underline" data-testid="home-all-treatments">Tutti i trattamenti →</Link>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {cats.map((c, i) => <div key={c.id} className={i % 2 ? "lg:mt-12" : ""}><CategoryCard c={c} index={i} tall /></div>)}
          </div>
        </Container>
      </section>
      <Philosophy />
      {featured.length > 0 && (
        <section className="py-24">
          <Container>
            <SectionTitle eyebrow="In evidenza" title="Trattamenti richiesti" />
            <div className="mt-12 grid gap-6 md:grid-cols-3">{featured.map((t) => <TreatmentCard key={t.id} t={t} />)}</div>
          </Container>
        </section>
      )}
      <GalleryTeaser />
      <Visit site={site} />
    </>
  );
}
